import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { analisesAPI, templatesAPI, projetosAPI } from '../../services/inovacaoAPI';
import { authAPI, setoresAPI } from '../../services/api';
import { mensagemErro } from './labels';

const F = { marginBottom: 16 };
const L = { display: 'block', marginBottom: 4, fontWeight: 500 };
const I = { width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc' };

export default function NovoProjeto() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const analiseParam = searchParams.get('analise');

  const [analisesAprovadas, setAnalisesAprovadas] = useState([]);
  const [analiseContexto, setAnaliseContexto] = useState(null); // veio de "Criar projeto desta análise"
  const [templates, setTemplates] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [mostrarOpcoes, setMostrarOpcoes] = useState(false);
  const [form, setForm] = useState({
    analise_id: '', nome: '', responsavel_tecnico_id: '',
    gerente_area_id: '', sponsor_id: '', setor_beneficiario_id: '',
    data_inicio_planejada: new Date().toISOString().slice(0, 10),
    buffer_percentual: 15, template_projeto_id: '',
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    templatesAPI.listar().then(setTemplates);
    authAPI.listUsers().then((r) => setUsuarios(r.data)).catch(() => {});
    setoresAPI.list().then((r) => setSetores(r.data)).catch(() => {});
    if (analiseParam) {
      // Fluxo pré-preenchido: veio da análise aprovada
      analisesAPI.obter(analiseParam).then((a) => {
        setAnaliseContexto(a);
        setForm((f) => ({
          ...f,
          analise_id: String(a.id),
          responsavel_tecnico_id: String(a.responsavel_tecnico?.id || a.executor_gni?.id || ''),
          gerente_area_id: String(a.gerente_area?.id || ''),
          setor_beneficiario_id: String(a.setor_solicitante?.id || ''),
        }));
      }).catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar a análise')));
    } else {
      // Fluxo avulso: lista as aprovadas pra escolher
      analisesAPI.listar({ estado: 'aprovada', size: 50 }).then((r) => setAnalisesAprovadas(r.items));
    }
  }, [analiseParam]);

  const semAnaliseDisponivel = !analiseParam && analisesAprovadas.length === 0;
  const podeSubmeter = !!form.analise_id && !salvando;

  async function submit(e) {
    e.preventDefault();
    const body = {
      ...form,
      analise_id: parseInt(form.analise_id),
      responsavel_tecnico_id: parseInt(form.responsavel_tecnico_id),
      gerente_area_id: parseInt(form.gerente_area_id),
      sponsor_id: form.sponsor_id ? parseInt(form.sponsor_id) : null,
      setor_beneficiario_id: form.setor_beneficiario_id ? parseInt(form.setor_beneficiario_id) : null,
      buffer_percentual: parseInt(form.buffer_percentual),
      template_projeto_id: form.template_projeto_id ? parseInt(form.template_projeto_id) : null,
    };
    setSalvando(true);
    try {
      const p = await projetosAPI.criar(body);
      toast.success('Projeto criado');
      navigate(`/inovacao/projetos/${p.id}`);
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao criar projeto'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ padding: 32, maxWidth: 760, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>Novo projeto</h1>
      <p style={{ color: '#7f8c8d', marginTop: 0 }}>
        Confirme o essencial; o resto já tem padrão. As fases vêm do template.
      </p>

      <form onSubmit={submit} style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
        {/* Origem: contexto travado (veio da análise) OU seletor (avulso) */}
        {analiseContexto ? (
          <div style={{ ...F, background: '#eafaf1', border: '1px solid #b9eccb', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: '0.78rem', color: '#1e7e45', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Projeto a partir da análise
            </div>
            <div style={{ color: '#16323a', fontWeight: 600, marginTop: 2 }}>
              #{analiseContexto.id} · {analiseContexto.titulo}
            </div>
          </div>
        ) : (
          <div style={F}>
            <label style={L}>Análise aprovada *</label>
            <select style={I} value={form.analise_id} required
                    onChange={(e) => setForm({ ...form, analise_id: e.target.value })}>
              <option value="">—</option>
              {analisesAprovadas.map((a) => (
                <option key={a.id} value={a.id}>#{a.id} · {a.titulo} (score {a.score_oportunidade})</option>
              ))}
            </select>
            {semAnaliseDisponivel && (
              <small style={{ color: '#e67e22' }}>
                Nenhuma análise aprovada. Conclua o fluxo de aprovação primeiro.
              </small>
            )}
          </div>
        )}

        {/* Template — modelo de etapas reutilizável (acelera, não é obrigatório) */}
        <div style={F}>
          <label style={{ ...L, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Template (modelo de etapas)</span>
            <Link to="/inovacao/templates" style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--brand, #20a490)' }}>gerenciar</Link>
          </label>
          <select style={I} value={form.template_projeto_id}
                  onChange={(e) => setForm({ ...form, template_projeto_id: e.target.value })}>
            <option value="">Nenhum (montar as fases manualmente)</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
          <small style={{ color: '#7f8c8d' }}>
            Modelo reutilizável de fases. As fases viram cópias no projeto, e você ajusta nomes, horas e responsáveis depois.
          </small>
        </div>

        {/* Quem responde + quando começa */}
        <div style={{ ...F, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={L}>Responsável técnico *</label>
            <select style={I} value={form.responsavel_tecnico_id} required
                    onChange={(e) => setForm({ ...form, responsavel_tecnico_id: e.target.value })}>
              <option value="">—</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
            </select>
          </div>
          <div>
            <label style={L}>Gerente da área *</label>
            <select style={I} value={form.gerente_area_id} required
                    onChange={(e) => setForm({ ...form, gerente_area_id: e.target.value })}>
              <option value="">—</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
            </select>
          </div>
        </div>

        <div style={F}>
          <label style={L}>Data de início *</label>
          <input type="date" style={{ ...I, maxWidth: 220 }} value={form.data_inicio_planejada} required
                 onChange={(e) => setForm({ ...form, data_inicio_planejada: e.target.value })} />
        </div>

        {/* Mais opções (colapsado) — defaults sensatos, raramente precisam mudar */}
        <button type="button" onClick={() => setMostrarOpcoes((o) => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
                         color: 'var(--brand, #20a490)', cursor: 'pointer', fontSize: '0.88rem', padding: '4px 0', marginBottom: 8 }}>
          {mostrarOpcoes ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Mais opções
        </button>

        {mostrarOpcoes && (
          <div style={{ borderLeft: '2px solid #eef2f4', paddingLeft: 16, marginBottom: 16 }}>
            <div style={F}>
              <label style={L}>Nome do projeto <span style={{ color: '#95a5a6', fontWeight: 400 }}>(vazio = usa o título da análise)</span></label>
              <input style={I} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </div>
            <div style={{ ...F, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={L}>Sponsor (decisor)</label>
                <select style={I} value={form.sponsor_id}
                        onChange={(e) => setForm({ ...form, sponsor_id: e.target.value })}>
                  <option value="">Sem sponsor</option>
                  {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
                </select>
              </div>
              <div>
                <label style={L}>Setor beneficiário</label>
                <select style={I} value={form.setor_beneficiario_id}
                        onChange={(e) => setForm({ ...form, setor_beneficiario_id: e.target.value })}>
                  <option value="">Mesmo que o solicitante</option>
                  {setores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            </div>
            <div style={{ ...F, maxWidth: 160 }}>
              <label style={L}>Buffer (%)</label>
              <input type="number" min="0" max="50" style={I} value={form.buffer_percentual}
                     onChange={(e) => setForm({ ...form, buffer_percentual: e.target.value })} />
            </div>
          </div>
        )}

        <button type="submit" disabled={!podeSubmeter}
                title={!form.analise_id ? 'Selecione/aprove uma análise primeiro' : undefined}
                style={{ background: !podeSubmeter ? '#bdc3c7' : 'var(--brand, #20a490)', color: !podeSubmeter ? '#fff' : 'var(--brand-ink, #fff)',
                         padding: '11px 26px', border: 'none', borderRadius: 6, fontWeight: 600,
                         cursor: !podeSubmeter ? 'not-allowed' : 'pointer' }}>
          {salvando ? 'Criando…' : 'Criar projeto'}
        </button>
      </form>
    </div>
  );
}
